import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Career, CareerOverviewCard } from 'labxchange-client';
import { WrappedMessage } from 'utils';
import { Icon } from 'elements';
import classNames from 'classnames';
import { formatSalary } from '../CareerCard';
import { ROUTES } from '../../../../global/constants';
import messages from './displayMessages';
import { intl } from 'i18n';

interface CareerListProps {
  careers: Career[];
  onRemove: (career: Career) => void;
}

const CareerList: React.FC<CareerListProps> = ({ careers, onRemove }) => {
  const [expandedRows, setExpandedRows] = useState<{ [key: string]: boolean }>(
    {}
  );

  const orderedCareers = careers.sort((a, b) => {
    if (a.title < b.title) {
      return -1;
    }
    if (a.title > b.title) {
      return 1;
    }
    return 0;
  });

  const [expandedDescriptions, setExpandedDescriptions] = useState<{
    [key: string]: boolean;
  }>({});
  const [showReadMore, setShowReadMore] = useState<{ [key: string]: boolean }>(
    {}
  );

  const toggleExpand = (slug: string) => {
    setExpandedRows((prev) => {
      const newExpandedRows = { ...prev, [slug]: !prev[slug] };
      setTimeout(() => {
        const ref = descriptionRefs.current[slug];
        if (ref) {
          setShowReadMore((previous) => ({
            ...previous,
            [slug]: ref.scrollHeight > ref.clientHeight,
          }));
        }
      }, 0);

      return newExpandedRows;
    });
  };

  const getCardLabelByType = (
    cards: CareerOverviewCard[],
    type: string
  ): string | null => {
    const selectedCard = cards.find((card) => card.cardType === type);
    return selectedCard?.cardLabel ? selectedCard.cardLabel : null;
  };

  const toggleDescriptionExpand = (slug: string) => {
    setExpandedDescriptions((previous) => ({
      ...previous,
      [slug]: !previous[slug],
    }));
  };

  const descriptionRefs = useRef<{
    [key: string]: HTMLParagraphElement | null;
  }>({});

  return (
    <div className='saved-careers-page'>
      <h2 className='saved-careers-title'>
        <WrappedMessage message={messages.savedCareersTitle} />
      </h2>

      {careers.length === 0 ? (
        <p className='no-saved-careers-message'>
          <WrappedMessage message={messages.noSavedCareers} />
        </p>
      ) : (
        <div className='career-list-container'>
          <div className='career-list-header'>
            <div className='career-header-item'>
              {intl.formatMessage(messages.careerTitle)}
            </div>
            <div className='career-header-item'>
              {' '}
              {intl.formatMessage(messages.requiredEducation)}
            </div>
            <div className='career-header-item'>
              {' '}
              {intl.formatMessage(messages.averageSalary)}
            </div>
          </div>
          {orderedCareers.map((career) => (
            <div className='career-row' key={career.slug}>
              <div className='basic-info'>
                <div className='career-related-info'>
                  <div
                    className={classNames('column', {
                      bold: expandedRows[career.slug],
                    })}
                  >
                    <div className='column-text'>{career.title}</div>
                  </div>
                  <div className='column'>
                    <Icon className='icon' name={'education'} fill='#3E5DB3' />
                    <div className='column-text'>
                      {career.minimumQualification.careerCardInformation}
                    </div>
                  </div>
                  <div className='column'>
                    <Icon className='icon' name={'salary'} fill='#3E5DB3' />
                    <div className='column-text'>
                      {career.entryLevelSalary ? (
                        formatSalary(career.entryLevelSalary)
                      ) : (
                        <WrappedMessage message={messages.noDataAvailable} />
                      )}
                    </div>
                  </div>
                </div>
                <div className='career-actions'>
                  <button
                    className='remove-button'
                    onClick={() => onRemove(career)}
                  >
                    <Icon name={'bookmark-filled'} fill='#003E6B' />
                    <span className='remove-text'>
                      <WrappedMessage message={messages.removeButtonText} />
                    </span>
                  </button>
                  <button
                    className='expand-button'
                    onClick={() => toggleExpand(career.slug)}
                    aria-expanded={expandedRows[career.slug]}
                    data-testid={`expand-button-${career.slug}`}
                  >
                    {expandedRows[career.slug] ? (
                      <Icon name={'accordion-opened'} fill='#497595' />
                    ) : (
                      <Icon name={'accordion-closed'} fill='#497595' />
                    )}
                  </button>
                </div>
              </div>
              {expandedRows[career.slug] && (
                <div className='career-more-details'>
                  <div className='detail-entry'>
                    <Icon className='icon' name={'education'} fill='#497595' />
                    {career.minimumQualification.qualificationName}
                  </div>
                  <div className='detail-entry'>
                    <Icon className='icon' name={'salary'} fill='#497595' />
                    {career.entryLevelSalary ? (
                      formatSalary(career.entryLevelSalary)
                    ) : (
                      <WrappedMessage message={messages.noDataAvailable} />
                    )}
                  </div>
                  {career.overviewCards && (
                    <div className='detail-entry'>
                      <Icon name='duties' fill='#3E5DB3' />
                      {getCardLabelByType(career.overviewCards, 'duties')}
                    </div>
                  )}

                  <div className='detail-entry'>
                    <Icon name='entry-level-jobs' fill='#3E5DB3' />
                    {career.subcategories?.length ? (
                      <>
                        {career.subcategories[0].category.categoryName}
                        {', '}
                        {career.subcategories
                          .map((sub) => sub.subcategoryName)
                          .join(', ')}
                      </>
                    ) : (
                      <WrappedMessage message={messages.noDefinedCategory} />
                    )}
                  </div>
                  <div className='detail-entry description'>
                    <span>
                      <Icon
                        name='associate'
                        fill='#3E5DB3'
                        className='description-icon'
                      ></Icon>
                    </span>
                    <span className='career-description'>
                      <p
                        className={`career-text ${expandedDescriptions[career.slug] ? 'expanded' : ''}`}
                        ref={(el) =>
                          (descriptionRefs.current[career.slug] = el)
                        }
                      >
                        {career.description}
                      </p>
                      {showReadMore[career.slug] && (
                        <button
                          className='read-more'
                          onClick={() => toggleDescriptionExpand(career.slug)}
                        >
                          {expandedDescriptions[career.slug]
                            ? 'Read less'
                            : 'Read more'}
                        </button>
                      )}
                      <div className='description-divider'></div>
                      <Link
                        to={ROUTES.CareerExplorer.CAREER_DETAILS_SLUG(
                          career.slug
                        )}
                        className='view-career'
                      >
                        View career{' '}
                        <Icon
                          name='arrow-right-small-pointer'
                          fill='#3E5DB3'
                          className='arrow-icon'
                        />
                      </Link>
                    </span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CareerList;
